import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactElement,
  MutableRefObject,
} from 'react';
import { Box } from '@mui/material';
import {
  DataGridPro,
  GridApiPro,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { store } from '@src/store';
import useGridSubscription from '@src/hooks/useGridSubscription';
import { DeviceDataTableProps } from './types';
import { getColumns } from './columnUtils';
import { getRows } from './rowUtils';
import useAlias from '@alias';

const DeviceDataTable: React.FC<DeviceDataTableProps> = ({
  checkboxSelection,
  disableMultipleRowSelection = false,
  onRowSelectionChange,
  unitSelectionModel,
  dataPoints,
  pointConfigRefs,
  enableCmdBtnColumn,
  openCmdModal,
  devices = [],
  mqttClient,
  allowExpandedCount,
  initialExpandedDetailPanelIds,
  boxStyle,
  additionalClassnames,
  ...restProps
}): ReactElement => {
  const alias = useAlias();

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    GridRowId[]
  >(initialExpandedDetailPanelIds || []);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds: GridRowId[]) => {
      if (allowExpandedCount && newIds.length > allowExpandedCount) {
        setDetailPanelExpandedRowIds(newIds.slice(-1 * allowExpandedCount));
      } else {
        setDetailPanelExpandedRowIds(newIds);
      }
    },
    [allowExpandedCount],
  );

  const { isControlsLocked } = store.getState().service;

  const apiRef: MutableRefObject<GridApiPro> = useGridApiRef();

  const columns = useMemo(
    () =>
      getColumns(
        pointConfigRefs,
        enableCmdBtnColumn,
        isControlsLocked,
        openCmdModal,
        alias,
      ),
    [
      pointConfigRefs,
      enableCmdBtnColumn,
      isControlsLocked,
      openCmdModal,
      alias,
    ],
  );

  const rows = useMemo(
    () => getRows(dataPoints, pointConfigRefs, devices),
    [dataPoints, pointConfigRefs, devices],
  );

  const initialState = useMemo(() => {
    const expandedRowIds = initialExpandedDetailPanelIds || [];
    return {
      columns: {
        columnVisibilityModel: {
          id: false,
        },
      },
      detailPanel: {
        expandedRowIds,
      },
    };
  }, [initialExpandedDetailPanelIds]);

  const handleRowSelectionChange = useCallback(
    (rowSelectionModel: GridRowSelectionModel): void => {
      if (onRowSelectionChange) {
        onRowSelectionChange(rowSelectionModel);
      }
    },
    [onRowSelectionChange],
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  useGridSubscription(rows, apiRef, mqttClient);

  useEffect(() => {
    if (initialExpandedDetailPanelIds) {
      setDetailPanelExpandedRowIds(initialExpandedDetailPanelIds);
    }
  }, [initialExpandedDetailPanelIds]);

  const boxStyles = useMemo(
    () => ({ height: 'calc(100% - 47px)', ...boxStyle }),
    [boxStyle],
  );

  const className = useMemo(
    () => `MuiDataGrid-DeviceDataTable ${additionalClassnames || ''}`.trim(),
    [additionalClassnames],
  );

  return (
    <Box sx={boxStyles}>
      <DataGridPro
        rowSelectionModel={
          unitSelectionModel !== undefined ? unitSelectionModel : undefined
        }
        throttleRowsMs={1000}
        onRowSelectionModelChange={handleRowSelectionChange}
        checkboxSelection={checkboxSelection}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        {...restProps}
        initialState={initialState}
        className={className}
        disableMultipleRowSelection={disableMultipleRowSelection}
        columns={columns}
        rows={rows}
        apiRef={apiRef}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        onDetailPanelExpandedRowIdsChange={
          handleDetailPanelExpandedRowIdsChange
        }
      />
    </Box>
  );
};

DeviceDataTable.displayName = 'DeviceDataTable';

export default memo(DeviceDataTable, (prevProps, nextProps) => {
  return (
    prevProps.dataPoints === nextProps.dataPoints &&
    prevProps.pointConfigRefs === nextProps.pointConfigRefs &&
    prevProps.devices === nextProps.devices &&
    prevProps.checkboxSelection === nextProps.checkboxSelection &&
    prevProps.disableMultipleRowSelection ===
      nextProps.disableMultipleRowSelection &&
    prevProps.enableCmdBtnColumn === nextProps.enableCmdBtnColumn &&
    prevProps.openCmdModal === nextProps.openCmdModal &&
    prevProps.unitSelectionModel === nextProps.unitSelectionModel &&
    prevProps.rowSelectionModel === nextProps.rowSelectionModel &&
    prevProps.allowExpandedCount === nextProps.allowExpandedCount &&
    prevProps.initialExpandedDetailPanelIds ===
      nextProps.initialExpandedDetailPanelIds &&
    prevProps.mqttClient === nextProps.mqttClient &&
    prevProps.onRowSelectionChange === nextProps.onRowSelectionChange &&
    prevProps.boxStyle === nextProps.boxStyle &&
    prevProps.additionalClassnames === nextProps.additionalClassnames
  );
});

export { getRows } from './rowUtils';
export type { DeviceDataTableRow, DeviceDataTableProps } from './types';
